import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { subscribeToAnnotationsForProject } from '../utils/pdfAnnotationService';
import { architectDesignTaskCommentsQuery } from '../utils/architectDesignCommentQuery';
import type {
  Milestone,
  PdfAnnotation,
  ProjectOnboarding,
  ProjectTask,
  ProjectTimeEntry,
  TaskComment,
  TaskGroup,
} from '../types';

/**
 * Project-scoped Hub data — subscribed only while a project is selected,
 * replacing the org-wide CRMContext streams for the Project Hub detail view.
 *
 * Tasks / groups / onboarding cover the deal's sibling projects too (group
 * comment threads span the precon + contracted phases of one deal), while
 * annotations / milestones / time entries are per selected project.
 */
export type ProjectHubScopedData = {
  projectId: string;
  tasks: ProjectTask[];
  taskGroups: TaskGroup[];
  taskComments: TaskComment[];
  projectOnboardings: ProjectOnboarding[];
  pdfAnnotations: PdfAnnotation[];
  milestones: Milestone[];
  timeEntries: ProjectTimeEntry[];
  /** True once every subscription has delivered its first snapshot. */
  ready: boolean;
};

// --- Firestore value mapping (mirrors CRMContext conversions) -----------------

const MISSING_DATE = new Date('2000-01-01T00:00:00Z');

function toDate(value: unknown): Date {
  const d = toOptionalDate(value);
  return d ?? MISSING_DATE;
}

function toOptionalDate(value: unknown): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  const maybe = value as { toDate?: () => Date };
  if (typeof maybe.toDate === 'function') return maybe.toDate();
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

function mapTask(id: string, data: Record<string, any>): ProjectTask {
  return {
    id,
    ...data,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    startDate: toDate(data.startDate),
    endDate: toDate(data.endDate),
    lastCommentAt: toOptionalDate(data.lastCommentAt),
    actualStartDate: toOptionalDate(data.actualStartDate),
    actualEndDate: toOptionalDate(data.actualEndDate),
    completedAt: toOptionalDate(data.completedAt),
    lockedAt: toOptionalDate(data.lockedAt),
    actionRequiredWaitSegments: Array.isArray(data.actionRequiredWaitSegments)
      ? data.actionRequiredWaitSegments.map((seg: Record<string, unknown>) => ({
          holderKey: String(seg.holderKey ?? ''),
          holderNote: seg.holderNote ? String(seg.holderNote) : undefined,
          startedAt: toDate(seg.startedAt),
          endedAt: seg.endedAt ? toDate(seg.endedAt) : undefined,
        }))
      : undefined,
  } as ProjectTask;
}

function mapComment(id: string, data: Record<string, any>): TaskComment {
  return {
    id,
    ...data,
    createdAt: toDate(data.createdAt),
    externalEmailDeliveries: Array.isArray(data.externalEmailDeliveries)
      ? data.externalEmailDeliveries.map((row: Record<string, any>) => ({
          ...row,
          sentAt: toDate(row.sentAt),
        }))
      : undefined,
    emailDeliveries: Array.isArray(data.emailDeliveries)
      ? data.emailDeliveries.map((row: Record<string, any>) => ({
          ...row,
          sentAt: toDate(row.sentAt),
        }))
      : undefined,
  } as TaskComment;
}

function mapOnboarding(id: string, data: Record<string, any>): ProjectOnboarding {
  return {
    id,
    ...data,
    templateVersion: toDate(data.templateVersion),
    createdAt: toDate(data.createdAt),
    completedAt: toOptionalDate(data.completedAt),
    items: (data.items || []).map((it: Record<string, any>) => ({
      ...it,
      completedAt: toOptionalDate(it.completedAt),
      startDate: toOptionalDate(it.startDate),
      endDate: toOptionalDate(it.endDate),
      actualStartDate: toOptionalDate(it.actualStartDate),
      actualEndDate: toOptionalDate(it.actualEndDate),
      lockedAt: toOptionalDate(it.lockedAt),
    })),
  } as ProjectOnboarding;
}

function mapMilestone(id: string, data: Record<string, any>): Milestone {
  return {
    id,
    ...data,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    targetDate: toDate(data.targetDate),
    completedDate: toOptionalDate(data.completedDate),
    lockedAt: toOptionalDate(data.lockedAt),
  } as Milestone;
}

function mapTimeEntry(id: string, data: Record<string, any>): ProjectTimeEntry {
  return {
    id,
    ...data,
    type: data.type ?? 'explicit',
    date: toDate(data.date),
    startTime: toOptionalDate(data.startTime),
    endTime: toOptionalDate(data.endTime),
    createdAt: toDate(data.createdAt),
  } as ProjectTimeEntry;
}

// --- Batched multi-project subscriptions --------------------------------------

const IN_QUERY_MAX = 10;

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Merge multi-query snapshots keyed by batch index; emits after every batch has reported once. */
function subscribeBatched<T>(
  ids: string[],
  makeQuery: (batch: string[]) => ReturnType<typeof query>,
  mapDoc: (id: string, data: Record<string, any>) => T,
  callback: (rows: T[], ready: boolean) => void,
): () => void {
  const batches = chunk(ids, IN_QUERY_MAX);
  if (batches.length === 0) {
    callback([], true);
    return () => {};
  }
  const byBatch = new Map<number, T[]>();
  const emit = () => {
    const rows: T[] = [];
    byBatch.forEach(list => rows.push(...list));
    callback(rows, byBatch.size === batches.length);
  };
  const unsubs = batches.map((batch, idx) =>
    onSnapshot(
      makeQuery(batch),
      snapshot => {
        byBatch.set(idx, snapshot.docs.map(d => mapDoc(d.id, d.data() as Record<string, any>)));
        emit();
      },
      () => {
        byBatch.set(idx, []);
        emit();
      },
    ),
  );
  return () => unsubs.forEach(u => u());
}

// --- Hook ----------------------------------------------------------------------

export function useProjectHubProjectData(
  projectId: string | null,
  dealId: string | null,
  siblingProjectIds: string[],
  options?: { decisionOnly?: boolean },
): ProjectHubScopedData | null {
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [taskGroups, setTaskGroups] = useState<TaskGroup[]>([]);
  const [taskComments, setTaskComments] = useState<TaskComment[]>([]);
  const [projectOnboardings, setProjectOnboardings] = useState<ProjectOnboarding[]>([]);
  const [pdfAnnotations, setPdfAnnotations] = useState<PdfAnnotation[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [timeEntries, setTimeEntries] = useState<ProjectTimeEntry[]>([]);
  const [readyFlags, setReadyFlags] = useState<Record<string, boolean>>({});

  const projectIdsKey = useMemo(() => {
    const ids = new Set(siblingProjectIds.filter(Boolean));
    if (projectId) ids.add(projectId);
    return Array.from(ids).sort().join(',');
  }, [projectId, siblingProjectIds]);

  // Tasks / groups / onboarding across the deal's sibling projects.
  useEffect(() => {
    if (!projectId) return;
    const ids = projectIdsKey.split(',').filter(Boolean);
    setTasks([]);
    setTaskGroups([]);
    setProjectOnboardings([]);
    setReadyFlags(prev => ({ ...prev, tasks: false, groups: false, onboarding: false }));

    const markReady = (key: string, ready: boolean) =>
      setReadyFlags(prev => (prev[key] === ready ? prev : { ...prev, [key]: ready }));

    const unsubTasks = subscribeBatched(
      ids,
      batch => query(collection(db, 'projectTasks'), where('projectId', 'in', batch)),
      mapTask,
      (rows, ready) => {
        setTasks(rows);
        markReady('tasks', ready);
      },
    );
    const unsubGroups = subscribeBatched(
      ids,
      batch => query(collection(db, 'taskGroups'), where('projectId', 'in', batch)),
      (id, data) => ({ id, ...data } as TaskGroup),
      (rows, ready) => {
        setTaskGroups([...rows].sort((a, b) => (a.position || 0) - (b.position || 0)));
        markReady('groups', ready);
      },
    );
    // Onboarding docs are keyed by projectId — subscribe to each directly.
    const onboardingRows = new Map<string, ProjectOnboarding | null>();
    const emitOnboarding = () => {
      setProjectOnboardings(
        Array.from(onboardingRows.values()).filter((ob): ob is ProjectOnboarding => !!ob),
      );
      markReady('onboarding', onboardingRows.size === ids.length);
    };
    const unsubOnboardings = ids.map(pid =>
      onSnapshot(
        doc(db, 'projectOnboarding', pid),
        snap => {
          onboardingRows.set(pid, snap.exists() ? mapOnboarding(snap.id, snap.data() as Record<string, any>) : null);
          emitOnboarding();
        },
        () => {
          onboardingRows.set(pid, null);
          emitOnboarding();
        },
      ),
    );

    return () => {
      unsubTasks();
      unsubGroups();
      unsubOnboardings.forEach(u => u());
    };
  }, [projectId, projectIdsKey]);

  // Comments: dealId scope (group threads span siblings) merged with projectId scope
  // (legacy per-task comments; backfillTaskCommentProjectIds stamps both fields).
  useEffect(() => {
    if (!projectId) return;
    setTaskComments([]);
    setReadyFlags(prev => ({ ...prev, comments: false }));

    const sources = new Map<string, TaskComment[]>();
    const seenSources = new Set<string>();
    const projectIds = projectIdsKey.split(',').filter(Boolean);
    const wanted = options?.decisionOnly
      ? (dealId ? projectIds.map((pid) => `architect:${pid}`) : [])
      : (dealId ? ['deal', 'project'] : ['project']);
    const emit = () => {
      const byId = new Map<string, TaskComment>();
      sources.forEach(rows => rows.forEach(c => c.id && byId.set(c.id, c)));
      const merged = Array.from(byId.values()).sort(
        (a, b) => a.createdAt.getTime() - b.createdAt.getTime(),
      );
      setTaskComments(merged);
      setReadyFlags(prev => {
        const ready = wanted.every(w => seenSources.has(w));
        return prev.comments === ready ? prev : { ...prev, comments: ready };
      });
    };

    const unsubs: Array<() => void> = [];
    const listen = (key: string, q: ReturnType<typeof query>) => {
      unsubs.push(
        onSnapshot(
          q,
          snapshot => {
            sources.set(key, snapshot.docs.map(d => mapComment(d.id, d.data() as Record<string, any>)));
            seenSources.add(key);
            emit();
          },
          () => {
            sources.set(key, []);
            seenSources.add(key);
            emit();
          },
        ),
      );
    };
    if (options?.decisionOnly) {
      if (dealId && projectIds.length) {
        for (const pid of projectIds) {
          listen(`architect:${pid}`, architectDesignTaskCommentsQuery(pid, dealId));
        }
      } else {
        setReadyFlags(prev => (prev.comments ? prev : { ...prev, comments: true }));
      }
    } else {
      if (dealId) listen('deal', query(collection(db, 'taskComments'), where('dealId', '==', dealId)));
      listen('project', query(collection(db, 'taskComments'), where('projectId', '==', projectId)));
    }

    return () => unsubs.forEach(u => u());
  }, [projectId, dealId, projectIdsKey, options?.decisionOnly]);

  // Annotations / milestones / time entries: selected project only.
  useEffect(() => {
    if (!projectId) return;
    setPdfAnnotations([]);
    setMilestones([]);
    setTimeEntries([]);
    setReadyFlags(prev => ({ ...prev, annotations: false, milestones: false, timeEntries: false }));

    const markReady = (key: string) =>
      setReadyFlags(prev => (prev[key] ? prev : { ...prev, [key]: true }));

    const unsubAnnotations = subscribeToAnnotationsForProject(
      projectId,
      anns => {
        setPdfAnnotations(anns);
        markReady('annotations');
      },
      { decisionOnly: !!options?.decisionOnly },
    );
    const unsubMilestones = onSnapshot(
      query(collection(db, 'milestones'), where('projectId', '==', projectId)),
      snapshot => {
        const rows = snapshot.docs
          .map(d => mapMilestone(d.id, d.data() as Record<string, any>))
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setMilestones(rows);
        markReady('milestones');
      },
      () => {
        setMilestones([]);
        markReady('milestones');
      },
    );
    const unsubTimeEntries = onSnapshot(
      query(collection(db, 'projectTimeEntries'), where('projectId', '==', projectId)),
      snapshot => {
        const rows = snapshot.docs
          .map(d => mapTimeEntry(d.id, d.data() as Record<string, any>))
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setTimeEntries(rows);
        markReady('timeEntries');
      },
      () => {
        setTimeEntries([]);
        markReady('timeEntries');
      },
    );

    return () => {
      unsubAnnotations();
      unsubMilestones();
      unsubTimeEntries();
    };
  }, [projectId, options?.decisionOnly]);

  return useMemo(() => {
    if (!projectId) return null;
    const ready = ['tasks', 'groups', 'onboarding', 'comments', 'annotations', 'milestones', 'timeEntries']
      .every(key => readyFlags[key]);
    return {
      projectId,
      tasks,
      taskGroups,
      taskComments,
      projectOnboardings,
      pdfAnnotations,
      milestones,
      timeEntries,
      ready,
    };
  }, [projectId, tasks, taskGroups, taskComments, projectOnboardings, pdfAnnotations, milestones, timeEntries, readyFlags]);
}

// --- Scoped-data context (Hub descendants prefer this over org-wide CRM arrays) --

const ProjectHubScopedDataContext = createContext<ProjectHubScopedData | null>(null);

export const ProjectHubScopedDataProvider = ProjectHubScopedDataContext.Provider;

/** Scoped Hub data when rendered inside a selected project; null elsewhere. */
export function useProjectHubScopedData(): ProjectHubScopedData | null {
  return useContext(ProjectHubScopedDataContext);
}
